/**
 * apiError.ts
 *
 * Retourne une CLÉ i18n (ou un message brut inconnu) à partir d'une erreur API.
 * On stocke cette clé dans le state et on appelle t(key) au rendu,
 * ce qui rend le message réactif aux changements de langue.
 *
 * Usage dans un composant :
 *   const [errorKey, setErrorKey] = useState<string | null>(null);
 *   ...
 *   } catch (err) {
 *     setErrorKey(getApiErrorKey(err, 'monModule.loadError'));
 *   }
 *   ...
 *   {errorKey && <p>{t(errorKey)}</p>}
 *
 * Cas particulier : si le backend renvoie un message inconnu (non mappé),
 * il est retourné tel quel — il ne sera pas réactif, mais c'est inévitable
 * puisqu'on n'a pas de clé i18n pour lui.
 */

// Table de correspondance : message PHP exact → clé i18n
const ERROR_MAP: Record<string, string> = {
  'Utilisateur non lié à un ERP': 'apiErrors.userNotLinkedToErp',
  'Veuillez sélectionner un client': 'apiErrors.pleaseSelectClient',
  'Aucun client associé à ce compte': 'apiErrors.noClientLinked',
  'Erreur de connexion ERP': 'apiErrors.erpConnectionError',
  'Erreur serveur': 'apiErrors.serverError',
}

export function getApiErrorKey(err: unknown, fallbackKey?: string): string {
  const apiErr = err as { response?: { data?: { error?: string } } }
  const message: string = apiErr?.response?.data?.error ?? ''

  // Cas spécial : "Route non trouvée: /xxx"
  if (typeof message === 'string' && message.startsWith('Route non trouvée')) {
    return 'apiErrors.routeNotFound'
  }

  // Message connu → retourne la clé i18n correspondante
  if (message && ERROR_MAP[message]) {
    return ERROR_MAP[message]
  }

  // Message inconnu mais non vide → retourné tel quel (message brut du backend)
  if (message) return message

  // Pas de message backend → clé de fallback ou clé générique
  return fallbackKey ?? 'apiErrors.unknown'
}
